/**
 * Morse — „Augen zu“: die reine Logik. (13.10.2026, Stufe 1 „Geführt“)
 *
 * Martin hört einen Buchstaben und SAGT ihn, ohne auf den Bildschirm zu sehen. Hier steht, was dabei entschieden wird —
 * ohne Seite, Ton und Mikrofon:
 *   - `waehleKandidat`: aus dem Gehörten den Buchstaben aus der GESCHLOSSENEN MENGE der gelernten Zeichen. Nächster Kandidat
 *     statt Lücke (der Leser morse-sprechen-leser.ts lässt Unsicheres leer, weil dort jemand tippt — hier tippt niemand);
 *     gleichauf oder zu knapp → „mehrdeutig“.
 *   - `urteile`: richtig, falsch, oder „nochmal“ ohne Strafe (Mehrdeutiges höchstens 2×, Stille 1×).
 *   - `neuesBuendel`: fünf Buchstaben wie `kochAufgabe` sie gewichtet (das neue Zeichen dreifach, Wackliges doppelt).
 *   - `fehlZeichen`, `quittungPlan`: was gebucht wird, wenn nichts kam, und der kurze Quittungston.
 *
 * ⚠️ Prior schlägt Messung nie ganz: ein klar gesprochener Buchstabe AUS der Menge gewinnt immer gegen seine Verwechsler
 *    (Em gegen En), aber nur mit Abstand (`MARGE`). Ein Buchstabe außerhalb der Menge zählt für seine Verwechsler IN der
 *    Menge, mit halbem Gewicht — ein „Ten“ bei K und M ist eher ein „Em“ als nichts.
 * ⚠️ `Wahl` trägt nur Zahlen und Art; der Buchstabe selbst verlässt diese Datei nur als `kandidat` an die Übung, nie in eine
 *    Messzeile. Einzige Ausnahme fürs Journal: `journalWort` (das Gehörte, ≤ 12 Buchstaben, nur ohne Kandidat).
 * ⚠️ Erwartung (`soll`) entscheidet NUR über die M-Laute („Hmm“, „Ähm“, `M_LAUTE`): sie gelten als M, wenn M dran ist, und
 *    sonst als nichts — nie als falsch.
 */
import { kochAufgabe, type Guete, type Umfang } from "../../shared/morse-koch.js";
import { pausen, type ZeitPlan } from "../../shared/morse.js";
import { merkwortBuchstabe, SPRECH_MERKWOERTER } from "../../shared/morse-sprech-merkwoerter.js";
import {
  BUCHSTABENNAMEN,
  BUCHSTABIERNAMEN,
  VERWECHSLUNG,
  deute,
  fehlhoerung,
  klingtWie,
  verwechslerVon,
  zerlege,
  type SprachWort,
} from "./morse-sprechen-leser.js";

/**
 * So lange (ms) nach dem Ende des Tons wartet die Übung auf die Antwort, bevor „nichts gehört“ gilt.
 * 23.10.2026 (Martin: „Reaktionszeit etwas erhöhen“): 6 s → 8 s.
 */
export const AUGEN_WARTE_MS = 8000;
/**
 * Länger nimmt eine Aufnahme in „Augen zu“ nie auf. Eigener Wert (nicht `HOECHSTENS_MS` des Sprechen-Knopfs, 8 s): wer spät
 * antwortet, soll nicht mitten im Wort abgeschnitten werden — immer mindestens 3 s nach `AUGEN_WARTE_MS`.
 */
export const AUGEN_HOECHSTENS_MS = AUGEN_WARTE_MS + 3000;
/** Nach dem Gesprochenen endet die Aufnahme nach so viel Stille (ein Buchstabe ist kurz — kürzer als beim Diktat). */
export const AUGEN_STILLE_MS = 850;
/**
 * Steht im Gehörten schon ein klarer Buchstabe der Menge (`waehleKandidat` → treffer), endet die Aufnahme nach so wenig
 * Stille — und wartet auf den Abschluss der Erkennung höchstens `AUGEN_FRUEH_ABSCHLUSS_MS` (die vorläufigen Wörter sind
 * schon da). Zusammen ≤ 0,8 s vom letzten neuen Wort bis zum Urteil. (15.10.2026, Martin: „die Auswertung dauert ewig“ —
 * bis dahin 850 ms Stille, neu aufgezogen von JEDER Meldung der Erkennung, plus bis zu 1,4 s Abschluss.)
 */
export const AUGEN_FRUEH_STILLE_MS = 300;
/** 23.10.2026 (Martin: „fast 1 Sekunde bis ein Feedback kommt“): 500 → 350 ms. */
export const AUGEN_FRUEH_ABSCHLUSS_MS = 350;
/**
 * Ist der klare Buchstabe von der Erkennung schon BESTÄTIGT (nichts Vorläufiges mehr, `DiktatRueckrufe.beiText` → `fest`), kann
 * der Abschluss ihn nicht mehr ändern: dann endet die Aufnahme nach so wenig Stille und wartet gar nicht auf den Abschluss
 * (23.10.2026). Bestätigt die Erkennung erst, wenn das Wort zu Ende ist, kommt dabei vorher fast immer schon mehr Text an; die
 * Restgefahr (ein bestätigtes „Em“, dann „il“ von „Emil“ erst nach 150 ms) misst die Messzeile (`abschluss-ms=0`).
 */
export const AUGEN_FEST_STILLE_MS = 150;
/** Nach so vielen Aufgaben hintereinander ohne Antwort pausiert die Runde (er ist eingeschlafen oder abgelenkt). */
export const AUGEN_OHNE_ANTWORT_PAUSE = 8;
/** Mehrdeutiges wird höchstens so oft noch einmal gespielt, Stille einmal. */
export const AUGEN_NOCHMAL_MEHRDEUTIG = 2;
export const AUGEN_NOCHMAL_STILLE = 1;
/** Ein Bündel = eine Koch-Aufgabe in der Buchung (wie beim Hören). */
export const AUGEN_BUENDEL = 5;

/** Ein Kandidat gilt ab dieser Punktzahl … */
export const MIN_PUNKTE = 0.3;
/** … und wenn er den zweiten um so viel schlägt. */
export const MARGE = 0.25;
/** Basis: Buchstabiername („Martha“) ist sicher, Buchstabenname („em“) und einzelner Buchstabe weniger. */
const PUNKTE_SICHER = 1;
const PUNKTE_BUCHSTABE = 0.85;
/** Ein Verwechsler IN der Menge bekommt von einem gehörten Buchstaben diesen Anteil mit (Em gegen En). */
const ANTEIL_NACHBAR_IN_MENGE = 0.3;
/** Ein gehörter Buchstabe AUSSERHALB der Menge zählt für seine Verwechsler in der Menge mit diesem Anteil. */
const ANTEIL_NACHBAR_AUSSEN = 0.5;
/** Ein ähnlich klingendes Wort (ein Zeichen daneben) zählt so viel. */
const PUNKTE_AEHNLICH = 0.45;
/** Mehr als ein Zeichen in einem Wort („km“): jedes zählt nur halb. */
const ANTEIL_MEHRERE = 0.5;
/**
 * Unter dieser Konfidenz ist ein Wort Rauschen und zählt gar nicht (die Aufgabe kommt noch einmal, nie „falsch“).
 *
 * 15.10.2026 (Martin: „K gesagt, es war K — nicht erkannt, dann falsch“; Journal: `erkannt=1 kandidat=0 sicher=100`): Bis
 * dahin wurden die PUNKTE mit der Konfidenz multipliziert. Ein einzeln gesprochener Buchstabe ist eine Silbe, die Erkennung
 * ist sich da selten sicher — ein klares „K“ mit 0,35 kam auf 0,85 × 0,35 < `MIN_PUNKTE`, obwohl kein anderer Buchstabe der
 * Menge im Spiel war (`sicher=100`). Seitdem entscheidet die Konfidenz nur noch ZWISCHEN Nachbarn (Em gegen En,
 * `nachbarAnteil`), nicht, ob überhaupt geantwortet wurde — außer unter dieser Grenze.
 */
export const KONFIDENZ_RAUSCHEN = 0.25;

/**
 * Welchen Anteil ein Verwechsler IN der Menge von einem gehörten Buchstaben mitbekommt: sicher gehört wenig
 * (`ANTEIL_NACHBAR_IN_MENGE`), unsicher gehört mehr — bei 0,4 liegen Em und En dann gleichauf (mehrdeutig, nochmal).
 */
function nachbarAnteil(konf: number | null): number {
  if (konf === null) return ANTEIL_NACHBAR_IN_MENGE;
  const k = Math.min(1, Math.max(0, konf));
  return ANTEIL_NACHBAR_IN_MENGE + (1 - ANTEIL_NACHBAR_IN_MENGE) * (1 - k);
}

type WahlArt = "treffer" | "mehrdeutig" | "unklar" | "stille";

export interface Wahl {
  /** treffer: ein Buchstabe gewinnt klar · mehrdeutig: zwei liegen gleichauf · unklar: Wörter gehört, aber nichts Passendes ·
   *  stille: nichts gehört. */
  art: WahlArt;
  /** Der gewählte Buchstabe — nur bei `treffer`. */
  kandidat: string | null;
  /** Wie klar (0…100): Abstand des Siegers zum Zweiten im Verhältnis zu seinen Punkten. Nur Zahl für die Messzeile. */
  sicher: number;
  /** Wie viele Wörter die Erkennung geliefert hat (Füllwörter mitgezählt). */
  woerter: number;
  /** Nur gesetzt, wenn der Treffer allein aus der Erwartung kam (ein M-Laut bei soll = M, `M_LAUTE`). */
  erwartet?: true;
  /** Nur bei `treffer`: das gesprochene Merkwort („monitor“), wenn der Buchstabe so gesagt wurde — für die Rückmeldung „M — Monitor“. */
  merkwort?: string;
  /**
   * Nur bei `mehrdeutig`/`unklar` (nie bei Lärm oder Stille): die 1–`AUGEN_KANDIDATEN_HOECHSTENS` Buchstaben der Menge, die er
   * wahrscheinlich meinte — die Übung zeigt sie als Tippfelder (21.10.2026). Nur aus dem GEHÖRTEN, nie aus `soll`.
   */
  kandidaten?: string[];
}

/** Höchstens so viele Tippfelder bei Unsicherheit. */
export const AUGEN_KANDIDATEN_HOECHSTENS = 4;
/** Im Mehrdeutigen zählt ein Buchstabe als Vorschlag, wenn er mindestens diesen Anteil der Punkte des Ersten hat. */
const KANDIDAT_ANTEIL = 0.5;

/**
 * Laute, die die Erkennung für ein einzeln gesprochenes „M“ schreibt — und die zugleich Füllwörter oder Summen sind.
 * (15.10.2026, Martin: „M“ gesagt → `erkannt=1 kandidat=0`.) Ein gesprochenes „Ämm“ kommt als „Ähm“, „Hmm“, „Mhm“ oder
 * „Mmm“ zurück. Dieselben Laute macht er beim Zögern — darum sind sie KEIN klarer Buchstabe:
 *   - sie zählen NUR als M, wenn M gerade dran ist (`soll`), M in der Menge ist und die Aufnahme aus nichts anderem besteht
 *     (höchstens zwei solche Laute, kein Füllwort, kein anderer Buchstabe daneben);
 *   - sie zählen NIE für einen anderen Buchstaben und nie als „falsch“: bei soll ≠ M bleiben sie „unklar“ (nochmal ohne Strafe).
 * „mm“/„mmm“ zählten vorher als M-M-M (mehrere Zeichen in einem Wort) — und damit als „falsch“, wenn er bei K summte.
 * Klare Schreibweisen („em“, „emm“, „äm“, „ämm“) stehen weiter in BUCHSTABENNAMEN und gelten immer.
 * Jede Schreibweise eine Zeile — keine Muster (`\b` kennt keine Umlaute).
 */
export const M_LAUTE: ReadonlySet<string> = new Set([
  "mm",
  "mmm",
  "mmmm",
  "mmmmm",
  "hm",
  "hmm",
  "hmmm",
  "mh",
  "mmh",
  "mhm",
  "mhmm",
  "ähm",
  "ähmm",
  "ehm",
  "ehmm",
  "ahm",
  "am",
]);
/** Mehr M-Laute als das in einer Aufnahme: das ist Summen oder Zögern, keine Antwort. */
const M_LAUTE_HOECHSTENS = 2;

/** Besteht die Aufnahme nur aus M-Lauten (höchstens zwei, mindestens einer über der Rauschgrenze)? */
function nurMLaute(stuecke: ReadonlyArray<{ t: string; konf: number | null }>): boolean {
  if (stuecke.length === 0 || stuecke.length > M_LAUTE_HOECHSTENS) return false;
  if (!stuecke.every((s) => M_LAUTE.has(s.t))) return false;
  return stuecke.some((s) => s.konf === null || s.konf >= KONFIDENZ_RAUSCHEN);
}

/** So lang darf das Gehörte in der Messzeile höchstens sein (Buchstaben, ohne Trenner) — sonst steht dort „…“. */
export const JOURNAL_WORT_HOECHSTENS = 12;
const JOURNAL_WORT_STUECKE = 3;

/**
 * Das Gehörte für die Messzeile (`wort=`), NUR wenn kein Buchstabe gewählt wurde: damit die echte Schreibweise eines
 * Buchstabens sichtbar wird, die in keiner Tabelle steht. Datenschutz: höchstens `JOURNAL_WORT_HOECHSTENS` Buchstaben in
 * höchstens drei Wörtern, nur Buchstaben (klein, Satzzeichen weg, Wörter mit „-“ verbunden) — alles andere, jeder Satz,
 * jede Ziffer wird „…“. Leer, wenn nichts gehört wurde.
 */
export function journalWort(eingabe: string | readonly SprachWort[]): string {
  const t = zerlege(eingabe).map((s) => s.t);
  if (t.length === 0) return "";
  const laenge = t.reduce((n, w) => n + [...w].length, 0);
  if (t.length > JOURNAL_WORT_STUECKE || laenge > JOURNAL_WORT_HOECHSTENS || !t.every((w) => /^\p{L}+$/u.test(w))) return "…";
  return t.join("-");
}

/** Alle Namen eines Zeichens, wie die Erkennung sie schreibt (klein). */
const NAMEN: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const t of [BUCHSTABIERNAMEN, BUCHSTABENNAMEN]) {
    for (const [name, z] of Object.entries(t)) m.set(z, [...(m.get(z) ?? []), name]);
  }
  return m;
})();

/** Kleinste Zahl an Änderungen zwischen zwei Wörtern (Levenshtein, kurze Wörter). */
export function abstand(a: string, b: string): number {
  const x = [...a];
  const y = [...b];
  let vor = Array.from({ length: y.length + 1 }, (_, j) => j);
  for (let i = 1; i <= x.length; i++) {
    const jetzt = [i];
    for (let j = 1; j <= y.length; j++) {
      jetzt[j] = Math.min(vor[j] + 1, jetzt[j - 1] + 1, vor[j - 1] + (x[i - 1] === y[j - 1] ? 0 : 1));
    }
    vor = jetzt;
  }
  return vor[y.length];
}

/** So viele Wörter (ohne Füllwörter), die kein klarer Buchstabenname sind, machen aus einer Antwort Lärm — mit Wörtern daneben. */
const LAERM_ALLEIN_AB = 4;

/**
 * Ist das eine Antwort — oder Lärm (Fernseher, Gespräch, fremde Sprache)? Ein Satz wie „und dann kam er“ enthält „er“ (= R) und
 * „kam“ (= K A M): gegen eine Menge allein ergäbe er einen sicheren Treffer. Darum zählt die FORM der Äußerung:
 *   - „klar“ ist ein Wort, das genau EIN Zeichen meint (Buchstabenname „em“, Buchstabierwort „Karl“, „K“, „acht“);
 *   - „fremd“ ist ein Wort, das nichts davon ist (ein anderes Wort, oder mehrere Zeichen auf einmal wie „kam“, „um“);
 *   - Füllwörter („und“, „dann“, „wie“, „ist“) zählen nicht.
 * Lärm: ein fremdes Wort NEBEN einem zweiten Wort („Karl Marx“, „emil ist da“) — oder viele klare Wörter (ab `LAERM_ALLEIN_AB`).
 * „K wie Karl“, „Karl“, „em“, „kilos“ (ein Wort, ein Zeichen daneben) bleiben Antworten. `lang`: die Aufnahme lief bis zum
 * Höchstwert (er redete und redete) — dann gilt schon jedes zweite Wort als Lärm.
 */
function istLaerm(stuecke: ReadonlyArray<{ t: string }>, drin: ReadonlySet<string>, lang: boolean): boolean {
  let bedeutsam = 0;
  let fremd = 0;
  for (const { t } of stuecke) {
    const d = deute(t, drin);
    if (d === "fuell") continue;
    bedeutsam++;
    if (d?.zeichen.length !== 1) fremd++;
  }
  if (lang && bedeutsam >= 2) return true;
  return (fremd >= 1 && bedeutsam >= 2) || bedeutsam >= LAERM_ALLEIN_AB;
}

/**
 * Höchstens so viele Wörter darf eine Äußerung haben, damit LEISE oder ÄHNLICHE Spuren Vorschläge geben (zwei: „hmm hmm“, zwei
 * leise Buchstaben). Die Fehlhörungs-Tabelle gilt strenger: nur bei GENAU EINEM Wort. Mehrere klar gehörte Buchstaben („K R“)
 * laufen nicht hierüber, sondern über die Punkte (Schritt 1).
 */
const KANDIDAT_WOERTER_HOECHSTENS = 2;

/**
 * Die Tippfelder bei Unsicherheit (21.10.2026, Martin: „dann kann ich zur Not drauf drücken“): welche Buchstaben der Menge er
 * wahrscheinlich meinte, der wahrscheinlichste zuerst, höchstens `AUGEN_KANDIDATEN_HOECHSTENS`.
 *   1. Hatten Buchstaben Punkte (mehrdeutig, oder zu knapp), sind es die mit mindestens `KANDIDAT_ANTEIL` des Ersten.
 *   2. Sonst — nur bei höchstens zwei Wörtern — die schwachen Spuren: ein klarer Buchstabe unter der Rauschgrenze
 *      (Journal: `wort=k` bei K, `wort=r` bei R, `wort=herr`), eine typische Fehlhörung („you“ → U, `EINZEL_FEHLHOERUNGEN` —
 *      nur, wenn die Äußerung GENAU EIN Wort ist), ein M-Laut („hmm“ → M), ein Wort einen Buchstaben neben einem Namen („ja“ ↔ „ka“).
 *   3. Aufgefüllt mit den Verwechslern IN der Menge (Em ↔ En), solange Platz ist.
 * ⚠️ Nichts davon kommt aus `soll` — sonst verrieten die Felder die Antwort.
 */
function kandidatenAus(stuecke: ReadonlyArray<{ t: string; konf: number | null }>, menge: readonly string[], rang: ReadonlyArray<[string, number]>): string[] {
  const drin = new Set(menge);
  const aus: string[] = [];
  const nimm = (z: string): void => {
    if (drin.has(z) && !aus.includes(z)) aus.push(z);
  };
  if (rang.length) {
    const p1 = rang[0][1];
    for (const [z, p] of rang) if (p >= p1 * KANDIDAT_ANTEIL) nimm(z);
  } else if (stuecke.length <= KANDIDAT_WOERTER_HOECHSTENS) {
    for (const { t } of stuecke) {
      const d = deute(t, drin);
      if (d !== null && d !== "fuell" && d.zeichen.length === 1) {
        const z = d.zeichen[0];
        if (drin.has(z)) nimm(z);
        else for (const n of menge) if (VERWECHSLUNG[z]?.includes(n) || VERWECHSLUNG[n]?.includes(z)) nimm(n);
      }
      if (stuecke.length === 1) for (const z of fehlhoerung(t)) nimm(z);
      if (M_LAUTE.has(t)) nimm("M");
      // Ein Zeichen neben einem Namen — nicht für Füllwörter („äh“ ist kein „äm“; „ja“ steht als Fehlhörung in der Tabelle).
      if ([...t].length >= 2 && d !== "fuell") for (const z of menge) if ((NAMEN.get(z) ?? []).some((n) => abstand(t, n) <= 1)) nimm(z);
    }
  }
  if (!aus.length) return [];
  for (const z of [...aus]) for (const n of menge) if (VERWECHSLUNG[z]?.includes(n) || VERWECHSLUNG[n]?.includes(z)) nimm(n);
  return aus.slice(0, AUGEN_KANDIDATEN_HOECHSTENS);
}

/** Die Wahl ohne Treffer — mit Tippfeldern, wenn es welche gibt (der Schlüssel fehlt sonst ganz). */
function ohneTreffer(art: "mehrdeutig" | "unklar", sicher: number, woerter: number, kandidaten: string[]): Wahl {
  return { art, kandidat: null, sicher, woerter, ...(kandidaten.length ? { kandidaten } : {}) };
}

/**
 * Aus dem Gehörten den Buchstaben der Menge. Wirft nie. `soll`: der Buchstabe, der gerade dran ist — er entscheidet nur
 * über die M-Laute (`M_LAUTE`), nie über einen klar gehörten Buchstaben. `vorsichtig`: die Erkennung hat das Gehörte selbst
 * wieder zurückgezogen (`ZuhoerErgebnis.nachgeholt`) — dann wird nie gewertet, ein Treffer wird zum Vorschlag (Tippfeld).
 */
export function waehleKandidat(
  eingabe: string | readonly SprachWort[],
  menge: readonly string[],
  o: { lang?: boolean; soll?: string; vorsichtig?: boolean } = {},
): Wahl {
  const stuecke = zerlege(eingabe);
  if (stuecke.length === 0) return { art: "stille", kandidat: null, sicher: 0, woerter: 0 };
  const drin = new Set(menge);
  // M-Laute zählen für Lärm und Punkte wie Füllwörter — sie sind nur über die Erwartung ein Buchstabe (unten).
  const rest = stuecke.filter((s) => !M_LAUTE.has(s.t));
  if (istLaerm(rest, drin, o.lang === true)) return { art: "unklar", kandidat: null, sicher: 0, woerter: stuecke.length };
  const punkte = new Map<string, number>();
  const gib = (z: string, p: number): void => {
    if (drin.has(z) && p > 0) punkte.set(z, (punkte.get(z) ?? 0) + p);
  };
  for (const { t, konf } of rest) {
    if (konf !== null && konf < KONFIDENZ_RAUSCHEN) continue;
    const d = deute(t, drin);
    if (d === "fuell") continue;
    if (d === null) {
      // Kein bekanntes Wort: klingt es wie der Name eines Buchstabens der Menge (ein Zeichen daneben)?
      if ([...t].length < 3) continue;
      let beste = Infinity;
      const treffer: string[] = [];
      for (const z of menge) {
        const naechste = Math.min(...(NAMEN.get(z) ?? []).map((n) => abstand(t, n)));
        if (naechste > 1) continue;
        if (naechste < beste) {
          beste = naechste;
          treffer.length = 0;
        }
        if (naechste === beste) treffer.push(z);
      }
      // Eine bekannte Fehlhörung („eks“ = X) zählt ähnlich nur für IHREN Buchstaben — nie für einen Nachbarn („eks“ ≈ „es“ = S,
      // Prüfer-Auflage 23.10.2026); „herr“ ≈ „err“ bleibt R.
      const fehl = fehlhoerung(t);
      for (const z of treffer) if (fehl.length === 0 || fehl.includes(z)) gib(z, PUNKTE_AEHNLICH);
      continue;
    }
    const basis = (d.quelle === "sicher" ? PUNKTE_SICHER : PUNKTE_BUCHSTABE) * (d.zeichen.length > 1 ? ANTEIL_MEHRERE : 1);
    // Mehrere Zeichen in einem Wort zählen je VERSCHIEDENEM Zeichen einmal (23.10.2026): sonst gewann in „see“ (S E E) das E,
    // in „kann“ das N, in „hallo“ das L — ein gewöhnliches Wort wurde ein sicherer Buchstabe, sobald alle Zeichen gelernt sind.
    // So liegen sie gleichauf (mehrdeutig, nochmal); „mm“ bleibt M.
    for (const z of new Set(d.zeichen)) {
      // Mehrere Zeichen in einem Wort („km“): jedes ist ein bloßer Buchstabe.
      const wie = d.zeichen.length > 1 ? z.toLowerCase() : t;
      if (drin.has(z)) {
        gib(z, basis);
        // Ein Buchstabenname hat Verwechsler: die in der Menge bekommen einen Teil mit (Buchstabiernamen verwechselt nichts) —
        // ein ausgeschriebener Name nur die, die gleich klingen („kah“ nicht G/T/Q, „em“ schon N; `verwechslerVon`, 23.10.2026).
        if (d.quelle === "buchstabe") for (const n of verwechslerVon(z, wie)) gib(n, basis * nachbarAnteil(konf));
      } else if (d.quelle === "buchstabe") {
        // Außerhalb der Menge: die Zeichen der Menge, die er verwechseln könnte (in beiden Richtungen der Tabelle) und die gleich
        // klingen. Ein Buchstabiername („Gustav“) ist eindeutig — er zählt für keinen anderen (bis 23.10.2026 war er ein halbes K).
        for (const n of menge) if ((VERWECHSLUNG[z]?.includes(n) || VERWECHSLUNG[n]?.includes(z)) && klingtWie(wie, n)) gib(n, basis * ANTEIL_NACHBAR_AUSSEN);
      }
    }
  }
  const rang = [...punkte].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const vorschlag = (): string[] => kandidatenAus(stuecke, menge, rang);
  if (rang.length === 0) {
    // Nichts Klares gehört — nur „Hmm“, „Ähm“, „Mmm“: bei soll = M ist das sein M, sonst bleibt es unklar (nie falsch).
    if (!o.vorsichtig && o.soll === "M" && drin.has("M") && nurMLaute(stuecke)) return { art: "treffer", kandidat: "M", sicher: 100, woerter: stuecke.length, erwartet: true };
    return ohneTreffer("unklar", 0, stuecke.length, vorschlag());
  }
  const [erster, p1] = rang[0];
  const p2 = rang[1]?.[1] ?? 0;
  const sicher = Math.round((100 * Math.max(0, p1 - p2)) / p1);
  if (p1 < MIN_PUNKTE) return ohneTreffer("unklar", sicher, stuecke.length, vorschlag());
  // Zurückgezogen gehört (vorsichtig) wertet nie: auch ein klarer Erster ist nur ein Vorschlag — mehrdeutig, mit Tippfeldern.
  if (p1 - p2 < MARGE || o.vorsichtig) return ohneTreffer("mehrdeutig", sicher, stuecke.length, vorschlag());
  const merkwort = rest.find((s) => merkwortBuchstabe(s.t) === erster)?.t;
  return { art: "treffer", kandidat: erster, sicher, woerter: stuecke.length, ...(merkwort ? { merkwort } : {}) };
}

/** Alle ganzen Wörter, die einen Buchstaben meinen (Buchstabier-, Merk-, Buchstabennamen) — für `kannNochWachsen`. */
const WOERTER_MIT_BUCHSTABE: ReadonlyArray<readonly [string, string]> = [
  ...Object.entries(BUCHSTABIERNAMEN),
  ...Object.entries(SPRECH_MERKWOERTER),
  ...Object.entries(BUCHSTABENNAMEN),
];

/**
 * Kann das zuletzt gehörte Wort noch zu einem ANDEREN Buchstaben der Menge werden? (23.10.2026, Prüfer-Auflage zum Schnellpfad
 * `AUGEN_FEST_STILLE_MS`.) Ein bestätigtes „Em“ ist der Anfang von „Emil“/„Emma“ (E), „Ze“ von „Zebra“ (Z), „Ca“ von „Caesar“
 * (C), „El“ von „Elefant“ (E): kommt der Rest erst nach 150 ms, wäre ein falsches Urteil gefallen. Dann wartet die Übung
 * auf den Abschluss (`AUGEN_FRUEH_ABSCHLUSS_MS`). Nur Wörter von Buchstaben IN der Menge zählen, nur echte (längere) Anfänge.
 */
export function kannNochWachsen(eingabe: string | readonly SprachWort[], menge: readonly string[]): boolean {
  const letzt = zerlege(eingabe).at(-1)?.t;
  if (!letzt) return false;
  const drin = new Set(menge);
  const d = deute(letzt, drin);
  const eigen = d !== null && d !== "fuell" && d.zeichen.length === 1 ? d.zeichen[0] : null;
  return WOERTER_MIT_BUCHSTABE.some(([w, z]) => z !== eigen && drin.has(z) && w.length > letzt.length && w.startsWith(letzt));
}

/* ── Urteil ───────────────────────────────────────────────────────── */

/** richtig/falsch: gebucht · nochmal: dieselbe Aufgabe, ohne Strafe · unklar: aufgelöst („Nicht verstanden. Das war K.“), NICHT gebucht. */
type UrteilArt = "richtig" | "falsch" | "nochmal" | "unklar";

export interface Urteil {
  art: UrteilArt;
  /** Was gebucht wird (nur bei richtig/falsch): der Buchstabe, den er sagte. Sonst leer. */
  ist: string;
  /** Hat er geantwortet — war ein Buchstabe der Menge im Spiel (auch ein falscher oder mehrdeutiger)? Sonst geht es in die Pause-Zählung. */
  beantwortet: boolean;
  /** Nur bei richtig: das gesprochene Merkwort („monitor“) — die Rückmeldung zeigt es („M — Monitor“). */
  merkwort?: string;
}

/** Wird dieses Urteil gebucht (zählt für Güte und Stufe)? Nur ein klar erkannter Buchstabe — richtig oder ein ANDERER. */
export function wirdGebucht(u: Urteil): boolean {
  return u.art === "richtig" || u.art === "falsch";
}

/**
 * Richtig, falsch, noch einmal — oder unklar. `nochmal`: wie oft diese Aufgabe schon wiederholt wurde.
 *   - treffer = soll → richtig; treffer ≠ soll → falsch (mit seinem Buchstaben) — „falsch“ gibt es NUR so;
 *   - stille: einmal wiederholen, dann unklar (nichts gehört);
 *   - mehrdeutig: bis zu zweimal wiederholen, dann unklar (er hat geantwortet, nur nicht eindeutig);
 *   - unklar (kein Treffer aus der Menge): bis zu zweimal wiederholen, dann unklar OHNE Antwort.
 * 15.10.2026 (Martin: „K gesagt, es war K — dann hieß es falsch“): bis dahin endeten Stille, Mehrdeutiges und Unverstandenes
 * mit „falsch“ (tiefer Ton, „Das war K.“) — gesagt hatte er aber nichts Falsches. Unklar wird ehrlich aufgelöst und nie gebucht.
 */
export function urteile(soll: string, w: Wahl, nochmal: number): Urteil {
  if (w.art === "treffer" && w.kandidat !== null) {
    return w.kandidat === soll ? { art: "richtig", ist: soll, beantwortet: true, ...(w.merkwort ? { merkwort: w.merkwort } : {}) } : { art: "falsch", ist: w.kandidat, beantwortet: true };
  }
  // Beantwortet heißt: es war ein Buchstabe der Menge im Spiel (mehrdeutig). Stille oder ohne Treffer (unklar: „äh“, Fernseher,
  // fremde Sprache) hat er nichts beantwortet — das zählt gegen die Pause.
  const beantwortet = w.art === "mehrdeutig";
  const grenze = w.art === "stille" ? AUGEN_NOCHMAL_STILLE : AUGEN_NOCHMAL_MEHRDEUTIG;
  return { art: nochmal < grenze ? "nochmal" : "unklar", ist: "", beantwortet };
}

/** Pausiert die Runde? `ohneAntwort`: wie viele Aufgaben in Folge ohne Antwort zu Ende gingen. */
export function pausiertOhneAntwort(ohneAntwort: number): boolean {
  return ohneAntwort >= AUGEN_OHNE_ANTWORT_PAUSE;
}

/* ── Aufgaben ─────────────────────────────────────────────────────── */

/**
 * Das nächste Bündel: fünf einzelne Buchstaben, gewichtet wie `kochAufgabe` (EINE Gruppe aus fünf Zeichen — das neue
 * Zeichen ist sicher dabei, Wackliges kommt doppelt). So ist jedes Bündel genau eine Koch-Aufgabe in der Buchung.
 */
export function neuesBuendel(stufe: number, o: { guete?: Guete; zufall?: () => number; umfang?: Umfang } = {}): string[] {
  const a = kochAufgabe(stufe, { art: "gruppen", anzahl: 1, guete: o.guete, zufall: o.zufall, umfang: o.umfang });
  return [...a.text.replace(/\s+/g, "")].slice(0, AUGEN_BUENDEL);
}

/**
 * Nach einem Fehler nicht gleich DERSELBE Buchstabe (23.10.2026, Martin: „Wenn ein Fehler gemacht wurde, muss der Buchstabe
 * nicht sofort wiederholt werden, weil danach sowieso wieder derselbe kommt“). Die Rückmeldung hat ihn eben genannt und langsam
 * vorgespielt; käme er als nächste Aufgabe, wäre sie geschenkt. Steht an `ab` wieder `zeichen`, tauscht er den Platz mit dem
 * ersten ANDEREN Buchstaben DESSELBEN Bündels dahinter — das Bündel (eine Koch-Aufgabe) behält genau seine Buchstaben, nur die
 * Reihenfolge ändert sich. Gibt es dort keinen anderen (alle gleich, oder `ab` ist die letzte Stelle des Bündels), bleibt es.
 * Der Fehler zählt weiter (gebucht, Güte sinkt) und kommt über die Gewichtung von `kochAufgabe` später wieder.
 * Gibt zurück, ob getauscht wurde. Ändert `gruppen` an Ort und Stelle.
 */
export function nichtGleichNachFehler(gruppen: string[], buendel: readonly number[], ab: number, zeichen: string): boolean {
  if (gruppen[ab] !== zeichen) return false;
  for (let j = ab + 1; j < gruppen.length && buendel[j] === buendel[ab]; j++) {
    if (gruppen[j] === zeichen) continue;
    [gruppen[ab], gruppen[j]] = [gruppen[j], gruppen[ab]];
    return true;
  }
  return false;
}

/* ── Anzeige des Buchstabens ───────────────────────────────────────── */

type AnzeigePhase = "hoert" | "spricht" | "rueckmeldung" | null;

/**
 * Steht der Buchstabe der Aufgabe jetzt auf dem Bildschirm? `aktiv`: eine Runde läuft oder ruht.
 *   - aus: nie · immer: immer, solange eine Runde da ist;
 *   - nach: erst in der Rückmeldung (nach der Antwort) — oder wenn er „Zeigen“ getippt hat (`gespickt`).
 * Während der Frage (hört, spricht) bleibt er sonst verdeckt.
 */
export function zeigtBuchstabe(modus: "aus" | "nach" | "immer", phase: AnzeigePhase, gespickt: boolean, aktiv: boolean): boolean {
  if (!aktiv || modus === "aus") return false;
  if (modus === "immer") return true;
  return gespickt || phase === "rueckmeldung";
}

/** Darf „Zeigen“ jetzt gedrückt werden — nur im Modus „nach“, während der Frage, einmal je Aufgabe? */
export function darfSpicken(modus: "aus" | "nach" | "immer", phase: AnzeigePhase, gespickt: boolean): boolean {
  return modus === "nach" && !gespickt && (phase === "hoert" || phase === "spricht");
}

/* ── Quittung ─────────────────────────────────────────────────────── */

const QUITTUNG_MS = 140;
/** Der „Weiter“-Tick vor einem NEUEN Buchstaben: kürzer und leiser als jede Quittung. */
const WEITER_MS = 70;
export const WEITER_LAUTSTAERKE = 0.07;
const QUITTUNG_WEITER_FAKTOR = 1.4;
export type QuittungsArt = "richtig" | "falsch" | "weiter";
/** Hell = deutlich über dem Übungston, tief = deutlich darunter (Hz-Grenzen halten es hörbar und angenehm). */
const QUITTUNG_HELL_FAKTOR = 1.8;
const QUITTUNG_TIEF_FAKTOR = 0.45;

/** Die Tonhöhe der Quittung zum eingestellten Übungston. */
export function quittungTon(art: QuittungsArt, ton: number): number {
  if (art === "weiter") return Math.min(2200, Math.round(ton * QUITTUNG_WEITER_FAKTOR));
  return art === "richtig" ? Math.min(2200, Math.round(ton * QUITTUNG_HELL_FAKTOR)) : Math.max(160, Math.round(ton * QUITTUNG_TIEF_FAKTOR));
}

/** Ein einzelner kurzer Ton als Plan (der Spieler morse-ton.ts spielt ihn wie jedes Zeichen). */
export function quittungPlan(art: QuittungsArt): ZeitPlan {
  const dauerMs = art === "weiter" ? WEITER_MS : art === "richtig" ? QUITTUNG_MS : QUITTUNG_MS * 2;
  return {
    toene: [{ beginnMs: 0, dauerMs, zeichen: 0 }],
    zeichen: [{ zeichen: art === "richtig" ? "+" : art === "falsch" ? "-" : ">", muster: ".", beginnMs: 0 }],
    gesamtMs: dauerMs,
    pausen: pausen(20, 20),
    unbekannt: [],
  };
}
